import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Trash2, Save, Loader2, Sparkles, Eye, EyeOff, GripVertical,
  ChevronDown, Tag,
} from "lucide-react";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

const CAPABILITIES = [
  { key: "scrape",          label: "Analizza Link (scraping URL)" },
  { key: "screenshot",      label: "Analisi Screenshot AI" },
  { key: "import_excel",    label: "Import Excel / CSV" },
  { key: "piano_economico", label: "Piano Economico avanzato" },
  { key: "board_chat",      label: "Chat sulle schede" },
  { key: "export_pdf",      label: "Export PDF report" },
];

const BADGE_OPTS = [
  { value: "",             label: "Nessun banner" },
  { value: "consigliato",  label: "Consigliato" },
  { value: "offerta",      label: "Offerta" },
  { value: "bestseller",   label: "Bestseller" },
  { value: "novita",       label: "Novità" },
];

const BADGE_STYLE = {
  consigliato: { bg: "#FFD60A", fg: "#1f2937" },
  offerta:     { bg: "#FF375F", fg: "#ffffff" },
  bestseller:  { bg: "#34C759", fg: "#ffffff" },
  novita:      { bg: "#AF52DE", fg: "#ffffff" },
};

const emptyPlan = () => ({
  slug: "", name: "", price_eur: 0, currency: "EUR", sort_order: 100,
  badge: "", annual_discount_pct: 0,
  features: [""], visible: true,
  capabilities: Object.fromEntries(CAPABILITIES.map((c) => [c.key, false])),
  limits: { max_boards: 5, max_properties: 10 },
});

// A single collapsible plan card — folded by default so the admin can eyeball
// the full plan list at a glance, drag them into order, then expand only the
// ones they need to edit.
const PlanCard = ({
  p, isDraft, onPatch, onSave, onDelete, onCreate, saving, creating,
  dragHandlers = {},
}) => {
  const setCap = (k, v) => onPatch({ capabilities: { ...(p.capabilities || {}), [k]: v } });
  const setLim = (k, v) => onPatch({ limits: { ...(p.limits || {}), [k]: v } });
  const setFeat = (i, v) => { const arr = [...(p.features || [])]; arr[i] = v; onPatch({ features: arr }); };
  const addFeat = () => onPatch({ features: [...(p.features || []), ""] });
  const rmFeat = (i) => { const arr = [...(p.features || [])]; arr.splice(i, 1); onPatch({ features: arr.length ? arr : [""] }); };
  const badgeStyle = BADGE_STYLE[p.badge];

  // Header (always visible) contents: drag handle · title · price · badge · visibility
  const header = (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {!isDraft && (
        <span
          className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 shrink-0 p-1"
          title="Trascina per riordinare"
          data-testid={`plan-drag-${p.slug}`}
          {...dragHandlers}
        >
          <GripVertical className="w-4 h-4" />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold text-base truncate">
            {p.name || <span className="text-slate-400 italic">Senza nome</span>}
          </span>
          {p.slug && (
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">{p.slug}</span>
          )}
          {badgeStyle && (
            <span
              className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: badgeStyle.bg, color: badgeStyle.fg }}
            >
              {p.badge}
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
          <span className="font-mono-num font-semibold">
            {new Intl.NumberFormat("it-IT", { style: "currency", currency: p.currency || "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(p.price_eur) || 0)}
          </span>
          <span className="text-slate-300">·</span>
          <span>{p.limits?.max_boards === -1 ? "∞" : (p.limits?.max_boards ?? 5)} schede</span>
          <span className="text-slate-300">·</span>
          <span>{p.limits?.max_properties === -1 ? "∞" : (p.limits?.max_properties ?? 10)} immobili</span>
        </div>
      </div>
    </div>
  );

  const body = (
    <div className="space-y-4 pt-4 border-t border-slate-100">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Slug</Label>
          <Input value={p.slug} onChange={(e) => onPatch({ slug: e.target.value.toLowerCase() })} disabled={!isDraft} className="rounded-lg h-9 bg-slate-50 border-slate-200 mt-1 font-mono-num text-xs" data-testid={isDraft ? "plan-draft-slug" : `plan-slug-${p.slug}`} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Nome</Label>
          <Input value={p.name} onChange={(e) => onPatch({ name: e.target.value })} className="rounded-lg h-9 bg-slate-50 border-slate-200 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Prezzo (€/mese)</Label>
          <Input type="number" step="0.01" value={p.price_eur} onChange={(e) => onPatch({ price_eur: parseFloat(e.target.value) || 0 })} className="rounded-lg h-9 bg-slate-50 border-slate-200 mt-1 font-mono-num" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Tag className="w-3 h-3" /> Banner promozionale
          </Label>
          <Select value={p.badge || ""} onValueChange={(v) => onPatch({ badge: v === "__none__" ? "" : v })}>
            <SelectTrigger className="rounded-lg h-9 bg-slate-50 border-slate-200 mt-1" data-testid={isDraft ? "plan-draft-badge" : `plan-badge-${p.slug}`}>
              <SelectValue placeholder="Nessun banner" />
            </SelectTrigger>
            <SelectContent>
              {BADGE_OPTS.map((o) => (
                <SelectItem key={o.value || "__none__"} value={o.value || "__none__"}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Sconto annuale % (12 mesi)</Label>
          <Input
            type="number" min={0} max={90} step={1}
            value={p.annual_discount_pct ?? 0}
            onChange={(e) => onPatch({ annual_discount_pct: Math.max(0, Math.min(90, parseInt(e.target.value) || 0)) })}
            className="rounded-lg h-9 bg-slate-50 border-slate-200 mt-1 font-mono-num"
            placeholder="0"
            data-testid={isDraft ? "plan-draft-annual-discount" : `plan-annual-discount-${p.slug}`}
          />
          {p.annual_discount_pct > 0 && Number(p.price_eur) > 0 && (
            <div className="mt-1 text-[10px] text-slate-500">
              Prezzo annuale: <span className="font-mono-num font-semibold text-emerald-600">
                {new Intl.NumberFormat("it-IT", { style: "currency", currency: p.currency || "EUR", minimumFractionDigits: 2 }).format(Number(p.price_eur) * 12 * (1 - p.annual_discount_pct / 100))}
              </span>
              <span className="text-slate-400"> anziché {new Intl.NumberFormat("it-IT", { style: "currency", currency: p.currency || "EUR", minimumFractionDigits: 2 }).format(Number(p.price_eur) * 12)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div />
        <div className="flex items-end justify-end gap-3">
          <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 cursor-pointer">
            <Switch checked={!!p.visible} onCheckedChange={(v) => onPatch({ visible: v })} data-testid={isDraft ? "plan-draft-visible" : `plan-visible-${p.slug}`} />
            {p.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {p.visible ? "Visibile" : "Nascosto"}
          </label>
          {!isDraft && (
            <Button variant="ghost" size="sm" className="rounded-full h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" onClick={() => onDelete(p.slug)} data-testid={`plan-delete-${p.slug}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Funzionalità sbloccate</div>
          <div className="space-y-1.5">
            {CAPABILITIES.map((c) => (
              <label key={c.key} className="flex items-center justify-between text-sm rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                <span>{c.label}</span>
                <Switch checked={!!p.capabilities?.[c.key]} onCheckedChange={(v) => setCap(c.key, v)} data-testid={isDraft ? `plan-draft-cap-${c.key}` : `plan-cap-${p.slug}-${c.key}`} />
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Limiti (-1 = illimitato)</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Max schede</Label>
                <Input type="number" value={p.limits?.max_boards ?? 5} onChange={(e) => setLim("max_boards", parseInt(e.target.value) || 0)} className="rounded-lg h-9 mt-1 bg-slate-50 border-slate-200 font-mono-num" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Max immobili</Label>
                <Input type="number" value={p.limits?.max_properties ?? 10} onChange={(e) => setLim("max_properties", parseInt(e.target.value) || 0)} className="rounded-lg h-9 mt-1 bg-slate-50 border-slate-200 font-mono-num" />
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Features (elenco)</div>
            <div className="space-y-1.5">
              {(p.features || [""]).map((f, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Input value={f} onChange={(e) => setFeat(i, e.target.value)} placeholder="es. Analizza Link illimitato" className="rounded-lg h-9 bg-slate-50 border-slate-200" />
                  <Button variant="ghost" size="sm" className="rounded-full h-7 w-7 p-0 text-slate-400 hover:text-rose-600" onClick={() => rmFeat(i)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="rounded-full h-7 text-slate-500 hover:text-slate-800" onClick={addFeat}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi feature
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        {isDraft ? (
          <Button onClick={onCreate} disabled={creating} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="plan-create">
            {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creo…</> : <><Plus className="w-4 h-4 mr-1.5" /> Crea piano</>}
          </Button>
        ) : (
          <Button onClick={() => onSave(p)} disabled={saving === p.slug} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid={`plan-save-${p.slug}`}>
            {saving === p.slug ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Salvo…</> : <><Save className="w-4 h-4 mr-1.5" /> Salva</>}
          </Button>
        )}
      </div>
    </div>
  );

  if (isDraft) {
    // Draft is always expanded — no fold. It's the "create plan" form.
    return (
      <div className="ios-card p-5" data-testid="plan-draft">
        <div className="flex items-center gap-2 mb-2">{header}</div>
        {body}
      </div>
    );
  }

  return (
    <Collapsible
      className={`ios-card p-4 ${!p.visible ? "opacity-70" : ""}`}
      data-testid={`plan-row-${p.slug}`}
    >
      <div className="flex items-center gap-2">
        {header}
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="rounded-full h-8 w-8 p-0 shrink-0 [&[data-state=open]>svg]:rotate-180" data-testid={`plan-toggle-${p.slug}`}>
            <ChevronDown className="w-4 h-4 transition-transform" />
          </Button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>{body}</CollapsibleContent>
    </Collapsible>
  );
};

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [draft, setDraft] = useState(emptyPlan());
  const [creating, setCreating] = useState(false);
  const [reordering, setReordering] = useState(false);
  // HTML5 drag-and-drop bookkeeping — plain refs so we don't re-render on
  // every dragover event.
  const dragFrom = useRef(null);
  const dragOver = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/plans");
      setPlans(r.data || []);
    } catch { toast.error("Errore caricamento piani"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchDraft = useCallback((patch) => setDraft((d) => ({ ...d, ...patch })), []);
  const patchExisting = useCallback((slug, patch) => setPlans((ps) => ps.map((p) => p.slug === slug ? { ...p, ...patch } : p)), []);

  const save = useCallback(async (p) => {
    setSaving(p.slug);
    try {
      await api.put(`/admin/plans/${p.slug}`, {
        name: p.name, price_eur: p.price_eur, sort_order: p.sort_order,
        badge: p.badge || "",
        annual_discount_pct: p.annual_discount_pct || 0,
        features: (p.features || []).filter((f) => f && f.trim()),
        visible: p.visible, capabilities: p.capabilities, limits: p.limits,
      });
      toast.success(`"${p.name}" aggiornato`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(""); }
  }, [load]);

  const remove = useCallback(async (slug) => {
    if (!window.confirm(`Eliminare il piano "${slug}"?`)) return;
    try { await api.delete(`/admin/plans/${slug}`); toast.success("Piano eliminato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  }, [load]);

  const create = useCallback(async () => {
    if (!draft.slug || !draft.name) { toast.error("slug e nome obbligatori"); return; }
    setCreating(true);
    try {
      await api.post("/admin/plans", {
        ...draft, badge: draft.badge || "",
        features: (draft.features || []).filter((f) => f && f.trim()),
      });
      toast.success("Piano creato");
      setDraft(emptyPlan());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setCreating(false); }
  }, [draft, load]);

  // Persist a new ordering on the server by PUT-ing sort_order for every plan
  // that changed position. Uses the same /admin/plans/{slug} endpoint.
  const persistOrder = useCallback(async (ordered) => {
    setReordering(true);
    try {
      await Promise.all(
        ordered.map((p, i) => {
          if (p.sort_order !== i * 10) {
            return api.put(`/admin/plans/${p.slug}`, { sort_order: i * 10 });
          }
          return Promise.resolve();
        })
      );
      toast.success("Ordine aggiornato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore ordinamento"); }
    finally { setReordering(false); }
  }, [load]);

  const onDragStart = (idx) => () => { dragFrom.current = idx; };
  const onDragOver = (idx) => (e) => { e.preventDefault(); dragOver.current = idx; };
  const onDrop = () => {
    const from = dragFrom.current, to = dragOver.current;
    dragFrom.current = null; dragOver.current = null;
    if (from == null || to == null || from === to) return;
    const next = [...plans];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPlans(next);
    persistOrder(next);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold mb-1"><Sparkles className="w-3 h-3 inline mr-1" /> Nuovo piano</div>
        <PlanCard p={draft} isDraft onPatch={patchDraft} onCreate={create} creating={creating} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">
            Piani esistenti ({plans.length}) — <span className="normal-case tracking-normal text-slate-400 font-normal">trascina <GripVertical className="w-3 h-3 inline -mt-0.5" /> per riordinare</span>
          </div>
          {reordering && <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Salvo ordine…</div>}
        </div>
        {loading && <div className="ios-card p-6 text-center text-slate-500 text-sm">Caricamento…</div>}
        {!loading && plans.length === 0 && <div className="ios-card p-6 text-center text-slate-500 text-sm">Nessun piano creato — usa il form sopra.</div>}
        <div className="space-y-3" data-testid="plans-list">
          {plans.map((p, i) => (
            <div
              key={p.slug}
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver(i)}
              onDrop={onDrop}
              onDragEnd={() => { dragFrom.current = null; dragOver.current = null; }}
            >
              <PlanCard
                p={p}
                onPatch={(patch) => patchExisting(p.slug, patch)}
                onSave={save}
                onDelete={remove}
                saving={saving}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPlans;
